/**
 * @file Asset detail field renderer.
 */

import { appData } from '../../app/state/state.js';
import {
    formatCurrency,
} from "../../shared/utils/displayUtils.js";
import {
    formatDate,
} from "../../shared/utils/dateUtils.js";
import {
    renderAuditTrail,
    renderLongTextDetailSection,
} from '../../shared/details/detailSections.js';
import {
    createDateControl,
} from '../../shared/ui/controls/dateControl.js';
import {
    createStatusSelectPreset,
    createTypeSelectPreset,
} from '../../shared/ui/controls/controlPresets.js';
import {
    AUTO_GROWING_TEXTAREA_CLASSES,
    DETAIL_TITLE_INPUT_CLASSES,
    NATIVE_INPUT_CLASSES,
    getNativeFieldWrapperHtml,
    getReadOnlyValueHtml,
} from '../../shared/forms/nativeFieldStyles.js';

function attrValue(value = '') {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function inputHtml(type, name, value = '', options = {}) {
    const placeholderAttr = options.placeholder ? ` placeholder="${attrValue(options.placeholder)}"` : '';
    const extraAttrs = options.extraAttrs ? ` ${options.extraAttrs}` : '';
    const className = [NATIVE_INPUT_CLASSES, options.className || '']
        .filter(Boolean)
        .join(' ');
    return `<input type="${type}" name="${name}" value="${attrValue(value)}" class="${className}"${placeholderAttr}${extraAttrs}>`;
}

function scalarControlRootHtml(fieldName) {
    return `<div class="min-w-0" data-detail-scalar-control="${fieldName}"></div>`;
}

/**
 * Generates HTML for editable Asset detail fields.
 * @param {object} item Asset item data.
 * @returns {string} Asset detail fields HTML.
 */
export function getAssetDetailFields(item) {
    const val = (field, fallback = '') => item?.[field] ?? fallback;

    return `
        <div class="bg-white pt-6 px-6 pb-3 rounded-lg shadow-md mb-6">
            <div class="mb-2 pb-1">
                <textarea name="name" class="${DETAIL_TITLE_INPUT_CLASSES} resize-none overflow-hidden" placeholder="Asset Name" aria-label="Asset Name" autocomplete="off" rows="1" data-auto-grow="true" required>${attrValue(val('name'))}</textarea>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-6 text-sm">
                ${getNativeFieldWrapperHtml('Asset Type(s)', scalarControlRootHtml('types'), { className: 'md:col-span-2' })}
                ${getNativeFieldWrapperHtml('Description', `<textarea name="description" placeholder="Colour, location, size, etc..." class="${AUTO_GROWING_TEXTAREA_CLASSES}" rows="1" data-auto-grow="true">${val('description')}</textarea>`, { className: 'md:col-span-2' })}

                ${getNativeFieldWrapperHtml('Serial Number', inputHtml('text', 'serial_number', val('serial_number')))}
                ${getNativeFieldWrapperHtml('Supplier', inputHtml('text', 'supplier', val('supplier')))}
                ${getNativeFieldWrapperHtml('Purchase Date', scalarControlRootHtml('purchase_date'))}
                ${getNativeFieldWrapperHtml('Purchase Price', inputHtml('text', 'purchase_price', formatCurrency(val('purchase_price')), { className: 'currency-input', placeholder: '$', extraAttrs: 'inputmode="decimal" data-native-currency="true"' }))}

                ${getNativeFieldWrapperHtml('Warranty', inputHtml('text', 'warranty', val('warranty')))}
                ${getNativeFieldWrapperHtml('Installer', inputHtml('text', 'installer', val('installer')))}
                ${getNativeFieldWrapperHtml('Installed On', scalarControlRootHtml('installed_on'))}
                ${getNativeFieldWrapperHtml('Service Frequency (Months)', inputHtml('number', 'servicing_requirements', val('servicing_requirements'), { extraAttrs: 'min="1" step="1" inputmode="numeric"' }))}

                ${getNativeFieldWrapperHtml('Status', scalarControlRootHtml('equipment_status'))}
                ${getNativeFieldWrapperHtml('Last Service Date', getReadOnlyValueHtml(formatDate(val('last_service_date'))))}
                ${getNativeFieldWrapperHtml('Next Service Due', getReadOnlyValueHtml(formatDate(val('next_service_due')), { id: 'asset-next-service-due' }))}
                <div></div>

                ${renderLongTextDetailSection({
        fieldName: 'comments',
        label: 'Details',
        value: val('comments'),
        wrapperClass: 'md:col-span-4',
    })}
            </div>
            ${renderAuditTrail(item)}
        </div>
    `;
}

/**
 * Mounts registered type controls for Asset details.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Asset item data.
 * @returns {object} Mounted control handles keyed by field name.
 */
export function setupAssetDetailTypeControls(detailsViewContainer, item) {
    const controls = {};
    const form = detailsViewContainer?.querySelector('#details-form');
    const root = form?.querySelector('[data-detail-scalar-control="types"]');
    if (!root) {return controls;}

    controls.types = createTypeSelectPreset({
        root,
        appData,
        itemType: 'asset',
        value: Array.isArray(item?.types) ? item.types : [],
        searchable: true,
    });

    return controls;
}

/**
 * Mounts registered scalar controls for Asset details.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Asset item data.
 * @returns {object} Mounted control handles keyed by field name.
 */
export function setupAssetDetailScalarControls(detailsViewContainer, item) {
    const controls = {};
    const form = detailsViewContainer?.querySelector('#details-form');
    if (!form) {return controls;}

    const getRoot = (fieldName) => form.querySelector(
        `[data-detail-scalar-control="${fieldName}"]`,
    );
    const value = (fieldName) => item?.[fieldName] ?? '';

    const purchaseDateRoot = getRoot('purchase_date');
    if (purchaseDateRoot) {
        controls.purchase_date = createDateControl({
            root: purchaseDateRoot,
            name: 'purchase_date',
            type: 'date',
            value: value('purchase_date'),
        });
    }

    const installedOnRoot = getRoot('installed_on');
    if (installedOnRoot) {
        controls.installed_on = createDateControl({
            root: installedOnRoot,
            name: 'installed_on',
            type: 'date',
            value: value('installed_on'),
        });
    }

    const equipmentStatusRoot = getRoot('equipment_status');
    if (equipmentStatusRoot) {
        controls.equipment_status = createStatusSelectPreset({
            root: equipmentStatusRoot,
            appData,
            itemType: 'asset',
            value: value('equipment_status'),
            clearable: false,
        });
    }

    return controls;
}
